import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { Database, Json } from '@/integrations/supabase/types';
import { safeError } from '@/lib/safeError';

type TeamRole = Database['public']['Enums']['team_role'];

export interface Team {
  id: string;
  name: string;
  slug: string | null;
  owner_id: string;
  logo_url: string | null;
  settings: Json | null;
  created_at: string;
  updated_at: string;
}

export interface TeamMembership {
  id: string;
  team_id: string;
  user_id: string;
  role: TeamRole;
  invited_by: string | null;
  invited_at: string | null;
  accepted_at: string | null;
  created_at: string;
  team: Team;
}

export interface TeamWithRole extends Team {
  role: TeamRole;
  member_count?: number;
}

export function useTeams() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [teams, setTeams] = useState<TeamWithRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingInvitations, setPendingInvitations] = useState<TeamMembership[]>([]);

  const fetchTeams = useCallback(async () => {
    if (!user) {
      setTeams([]);
      setPendingInvitations([]);
      setLoading(false);
      return;
    }

    try {
      // Fetch accepted memberships with team details
      const { data: memberships, error } = await supabase
        .from('team_members')
        .select(`
          id,
          team_id,
          user_id,
          role,
          status,
          invited_by,
          invited_at,
          accepted_at,
          created_at,
          teams (
            id,
            name,
            slug,
            owner_id,
            logo_url,
            settings,
            created_at,
            updated_at
          )
        `)
        .eq('user_id', user.id);

      if (error) throw error;

      const accepted: TeamWithRole[] = [];
      const pending: TeamMembership[] = [];

      (memberships || []).forEach((m: any) => {
        // Skip if no team data (shouldn't happen, but defensive)
        if (!m.teams) {
          console.warn('Membership missing team data:', m.id);
          return;
        }
        
        // Use status field to determine pending vs active
        if (m.status === 'active') {
          accepted.push({
            ...m.teams,
            role: m.role,
          });
        } else if (m.status === 'pending') {
          pending.push({
            ...m,
            team: m.teams,
          });
        }
      });

      setTeams(accepted);
      setPendingInvitations(pending);
    } catch (error: any) {
      console.error('Error fetching teams:', safeError(error));
      toast({
        title: 'Error loading teams',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  const createTeam = async (
    name: string, 
    options?: {
      description?: string;
      settings?: Record<string, unknown>;
      invitations?: Array<{ email: string; role: string }>;
      linkCompanyId?: string;
    }
  ): Promise<Team | null> => {
    if (!user) return null;

    try {
      // Create the team
      const teamInsertData = {
        name,
        slug: null as string | null,
        owner_id: user.id,
        settings: (options?.settings || null) as Json | null,
        company_id: (options?.linkCompanyId || null) as string | null,
      };
      
      const { data: team, error: teamError } = await supabase
        .from('teams')
        .insert(teamInsertData)
        .select()
        .single();

      if (teamError) throw teamError;

      // Add creator as owner member (auto-accepted)
      const memberInsertData = {
        team_id: team.id,
        user_id: user.id,
        role: 'owner' as const,
        invited_by: user.id,
        accepted_at: new Date().toISOString(),
        status: 'active',  // Owner is immediately active
      };

      const { error: memberError } = await supabase
        .from('team_members')
        .insert(memberInsertData);

      if (memberError) {
        // Clean up the orphaned team so we don't leave inconsistent state
        await supabase.from('teams').delete().eq('id', team.id);
        throw memberError;
      }

      // Send invitations if provided. Batch-resolve every invited email to an
      // existing user in a single query, then bulk-insert the pending members
      // (instead of an agents lookup + insert per invitation). Emails with no
      // matching user are skipped, unchanged from before.
      if (options?.invitations && options.invitations.length > 0) {
        try {
          const emails = options.invitations.map(i => i.email);
          const { data: invitedAgents } = await supabase
            .from('agents')
            .select('user_id, email')
            .in('email', emails);

          const userIdByEmail = new Map<string, string>();
          (invitedAgents || []).forEach(a => {
            if (a.email && a.user_id) userIdByEmail.set(a.email, a.user_id);
          });

          const invitedAt = new Date().toISOString();
          const memberRows = options.invitations
            .map(invite => {
              const invitedUserId = userIdByEmail.get(invite.email);
              if (!invitedUserId) return null;
              return {
                team_id: team.id,
                user_id: invitedUserId,
                role: invite.role as 'admin' | 'appraiser' | 'member' | 'viewer',
                invited_by: user.id,
                invited_at: invitedAt,
              };
            })
            .filter(Boolean) as Array<{
              team_id: string;
              user_id: string;
              role: 'admin' | 'appraiser' | 'member' | 'viewer';
              invited_by: string;
              invited_at: string;
            }>;

          if (memberRows.length > 0) {
            await supabase.from('team_members').insert(memberRows);
          }
        } catch (inviteError) {
          console.error('Error inviting users:', inviteError);
        }
      }

      toast({
        title: 'Team created',
        description: `"${name}" has been created successfully.`,
      });

      await fetchTeams();
      return team;
    } catch (error: any) {
      console.error('Error creating team:', safeError(error));
      toast({
        title: 'Error creating team',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    }
  };

  const updateTeam = async (
    teamId: string,
    updates: { name?: string; slug?: string; logo_url?: string; settings?: Json }
  ): Promise<boolean> => {
    // Verify caller has owner or admin role
    const membership = teams.find(t => t.id === teamId);
    if (!membership || (membership.role !== 'owner' && membership.role !== 'admin')) {
      toast({ title: 'Permission denied', description: 'Only team owners and admins can update team settings.', variant: 'destructive' });
      return false;
    }

    try {
      const { error } = await supabase
        .from('teams')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', teamId);

      if (error) throw error;

      toast({
        title: 'Team updated',
        description: 'Team settings have been saved.',
      });

      await fetchTeams();
      return true;
    } catch (error: any) {
      console.error('Error updating team:', safeError(error));
      toast({
        title: 'Error updating team',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    }
  };

  const deleteTeam = async (teamId: string): Promise<boolean> => {
    // Only the owner can delete a team
    const membership = teams.find(t => t.id === teamId);
    if (!membership || membership.role !== 'owner') {
      toast({ title: 'Permission denied', description: 'Only the team owner can delete a team.', variant: 'destructive' });
      return false;
    }

    try {
      const { error } = await supabase
        .from('teams')
        .delete()
        .eq('id', teamId);

      if (error) throw error;

      toast({
        title: 'Team deleted',
        description: 'The team has been permanently deleted.',
      });

      await fetchTeams();
      return true;
    } catch (error: any) {
      console.error('Error deleting team:', safeError(error));
      toast({
        title: 'Error deleting team',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    }
  };

  const acceptInvitation = async (membershipId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('team_members')
        .update({ 
          accepted_at: new Date().toISOString(),
          status: 'active'  // Also update status to active
        })
        .eq('id', membershipId);

      if (error) throw error;

      toast({
        title: 'Invitation accepted',
        description: 'You have joined the team.',
      });

      await fetchTeams();
      return true;
    } catch (error: any) {
      console.error('Error accepting invitation:', safeError(error));
      toast({
        title: 'Error accepting invitation',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    }
  };

  const declineInvitation = async (membershipId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('team_members')
        .delete()
        .eq('id', membershipId);

      if (error) throw error;

      toast({
        title: 'Invitation declined',
        description: 'The invitation has been removed.',
      });

      await fetchTeams();
      return true;
    } catch (error: any) {
      console.error('Error declining invitation:', safeError(error));
      toast({
        title: 'Error declining invitation',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    }
  };

  const leaveTeam = async (teamId: string): Promise<boolean> => {
    if (!user) return false;

    // Owners must transfer ownership before leaving
    const membership = teams.find(t => t.id === teamId);
    if (membership?.role === 'owner') {
      toast({ title: 'Cannot leave', description: 'Transfer ownership before leaving the team.', variant: 'destructive' });
      return false;
    }

    try {
      const { error } = await supabase
        .from('team_members')
        .delete()
        .eq('team_id', teamId)
        .eq('user_id', user.id);

      if (error) throw error;

      toast({
        title: 'Left team',
        description: 'You have left the team.',
      });

      await fetchTeams();
      return true;
    } catch (error: any) {
      console.error('Error leaving team:', safeError(error));
      toast({
        title: 'Error leaving team',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    }
  };

  const transferOwnership = async (teamId: string, newOwnerId: string): Promise<boolean> => {
    if (!user) return false;

    // Only owner can transfer
    const membership = teams.find(t => t.id === teamId);
    if (!membership || membership.role !== 'owner') {
      toast({ title: 'Permission denied', description: 'Only the team owner can transfer ownership.', variant: 'destructive' });
      return false;
    }

    try {
      // Use atomic RPC to transfer ownership in a single transaction
      const { error } = await supabase.rpc('transfer_team_ownership', {
        p_team_id: teamId,
        p_new_owner_id: newOwnerId,
      });

      if (error) throw error;

      toast({
        title: 'Ownership transferred',
        description: 'You are now an admin of this team.',
      });

      await fetchTeams();
      return true;
    } catch (error: any) {
      console.error('Error transferring ownership:', safeError(error));
      toast({
        title: 'Error transferring ownership',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    }
  };

  return {
    teams,
    loading,
    pendingInvitations,
    createTeam,
    updateTeam,
    deleteTeam,
    acceptInvitation,
    declineInvitation,
    leaveTeam,
    transferOwnership,
    refetch: fetchTeams,
  };
}
